/**
 * Session Storage Service
 *
 * Persists active search params (`dateRange`, `roomOccupancy`, `duration`) to
 * `sessionStorage` so the parameters survive a Back/Forward trip from
 * ProductListPage → OfferPage → back to ProductListPage. SessionStorage is
 * per-tab, so other tabs are unaffected.
 *
 * - **OfferPage** writes on every change to those three fields.
 * - **ProductListPage** reads only when navigation is detected as
 *   back/forward (see `useBackForwardNavigation`); otherwise the URL is the
 *   source of truth.
 *
 * The on-disk shape is intentionally string-based (ISO dates, PAX string)
 * so it parses safely after JSON round-trip and reuses the same validation
 * pipeline as URL parameters (`FilterValidation`, `parsePaxString`).
 *
 * @example
 * ```ts
 * SessionStorageService.write({
 *   dateRange: [new Date('2025-10-30'), new Date('2025-11-06')],
 *   roomOccupancy: { rooms: [{ persons: [{ type: 'adult' }] }] },
 *   duration: 7,
 * });
 *
 * const params = SessionStorageService.read();
 * // { dateRange: [Date, Date], roomOccupancy: {...}, duration: 7 }
 * ```
 */

import { FilterValidation } from '@/utils/validation/filterValidation';
import { parsePaxString, serializePaxString } from '@/utils/parsing/paxParser';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import type { DateRange, RoomOccupancy } from '@/types/SearchBar';
import { parseDurationMode, type DurationMode } from '@/utils/filters/durationMode';

export interface ActiveSearchParams {
  dateRange: DateRange;
  roomOccupancy: RoomOccupancy | null;
  duration: number | null;
  durationMode: DurationMode | null;
}

interface StoredShape {
  startDate?: string;
  endDate?: string;
  duration?: number;
  pax?: string;
  dmode?: string;
}

export class SessionStorageService {
  static readonly KEY = 'ibe_active_search_params';
  static readonly SRP_URL_KEY = 'ibe_srp_url';

  /**
   * Persist the current SRP (search results page) URL so the PDP can navigate
   * back to it. Written while on the SRP; read by the offer page's
   * "Back to search results" / "Start new search" actions.
   */
  static writeSrpUrl(url: string): void {
    try {
      sessionStorage.setItem(this.SRP_URL_KEY, url);
    } catch (error) {
      console.warn('[SessionStorageService] Failed to write SRP url:', error);
    }
  }

  /**
   * Read the persisted SRP URL, or null if none/unavailable.
   */
  static readSrpUrl(): string | null {
    try {
      return sessionStorage.getItem(this.SRP_URL_KEY);
    } catch (error) {
      console.warn('[SessionStorageService] sessionStorage unavailable:', error);
      return null;
    }
  }

  /**
   * Read and validate active search params from sessionStorage.
   */
  static read(): ActiveSearchParams | null {
    let raw: string | null;
    try {
      raw = sessionStorage.getItem(this.KEY);
    } catch (error) {
      console.warn('[SessionStorageService] sessionStorage unavailable:', error);
      return null;
    }
    if (!raw) return null;

    let parsed: StoredShape;
    try {
      parsed = JSON.parse(raw) as StoredShape;
    } catch (error) {
      console.warn('[SessionStorageService] Invalid JSON, ignoring:', error);
      return null;
    }

    const dateRange = this.parseDateRange(parsed);
    const duration =
      parsed.duration !== undefined ? FilterValidation.validateDuration(parsed.duration) : null;
    const roomOccupancy = parsed.pax ? parsePaxString(parsed.pax) : null;
    const durationMode = parseDurationMode(parsed.dmode) ?? null;

    if (!dateRange && duration === null && !roomOccupancy) {
      return null;
    }

    return { dateRange, roomOccupancy, duration, durationMode };
  }

  /**
   * Write active search params to sessionStorage. Silently no-ops on
   * QuotaExceededError or storage being unavailable (private browsing).
   */
  static write(params: ActiveSearchParams): void {
    const payload: StoredShape = {};

    if (params.dateRange && params.dateRange[0] && params.dateRange[1]) {
      payload.startDate = this.formatDate(params.dateRange[0]);
      payload.endDate = this.formatDate(params.dateRange[1]);
    }

    if (params.duration != null) {
      payload.duration = params.duration;
    }

    if (params.roomOccupancy && params.roomOccupancy.rooms.length > 0) {
      const pax = serializePaxString(params.roomOccupancy);
      if (pax) payload.pax = pax;
    }

    if (params.durationMode) {
      payload.dmode = params.durationMode;
    }

    if (Object.keys(payload).length === 0) {
      return;
    }

    try {
      sessionStorage.setItem(this.KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn('[SessionStorageService] Failed to write to sessionStorage:', error);
    }
  }

  /**
   * Remove the stored active search params.
   */
  static clear(): void {
    try {
      sessionStorage.removeItem(this.KEY);
    } catch {
      // Ignore — sessionStorage may be unavailable
    }
  }

  private static parseDateRange(parsed: StoredShape): DateRange {
    if (!parsed.startDate || !parsed.endDate) return null;

    const start = FilterValidation.validateDate(parsed.startDate);
    const end = FilterValidation.validateDate(parsed.endDate);
    if (!start || !end) return null;
    if (!FilterValidation.validateDateRange(start, end)) return null;

    return [start, end];
  }

  private static formatDate(date: Date): string {
    return formatDateLocal(date);
  }
}
